import { array, bool, object } from 'prop-types'
import { useContext } from 'react'

import localeContext from '../../../contexts/Locale'
import useTranslate from '../../../hooks/useTranslate'

import { isSmartHubService } from '../../../utils/homeSolutions'
import { isAffiliateCampaign } from '../../../utils/cart'

import NameAndPrice from '../../atoms/ItemDetails/NameAndPrice'
import {
  FinalPrice,
  ItemsPrice,
  OtherChargesAndCreditsContainer,
  PriceBreakdownContainer
} from '../../molecules/CartItem/PriceBreakdown'
import HeadingWithSubItems from '../../molecules/CartItem/PriceBreakdown/HeadingWithSubItems'
import {
  buildAdditionalChargesList,
  buildAdditionalCreditsAndDiscounts,
  getInternetMonthlyPrice,
  buildIncludedHardware,
  buildAdditionalHardware,
  isRouterWithEasyPayment,
  buildEssentialHardware,
  buildRecurringDiscounts,
  buildHSAddonsAdditionalCharges,
  buildMonthlyAddons
} from './utils'

const OfferDetails = ({
  internetService,
  internetAddons,
  compassDiscountItem,
  cart,
  featureFlags,
  IRPCParams,
  isBusinessCart
}) => {
  const { lang } = useContext(localeContext)
  const translate = useTranslate()

  const isInternetSmartHubService = isSmartHubService(internetService)
  const isSmartHubEasyPayment = isRouterWithEasyPayment(internetService)
  const isAffiliates = isAffiliateCampaign(cart)

  const shouldRenderEssentialHardwareInPriceBreakdown =
    isInternetSmartHubService && isSmartHubEasyPayment

  const shouldRenderEssentialHardwareInOtherChargesAndCredits =
    isInternetSmartHubService && !isSmartHubEasyPayment

  const monthlySubTotal = getInternetMonthlyPrice({
    internet: internetService,
    compassDiscountItem,
    isIrpcFlow: IRPCParams.isIrpcFlow
  })

  const includedInternetHardwareData = buildIncludedHardware(
    internetService,
    isInternetSmartHubService,
    lang
  )
  const additionalInternetHardwareData = buildAdditionalHardware(
    internetService,
    isInternetSmartHubService,
    lang
  )

  const essentialHardwareData = buildEssentialHardware({
    internet: internetService,
    isSmartHubEasyPayment,
    translate,
    lang
  })

  const recurringDiscountsData = buildRecurringDiscounts({
    internet: internetService,
    isIRPCFlow: IRPCParams.isIrpcFlow,
    isInternetSmartHubService,
    compassDiscountItem,
    featureFlags,
    translate,
    lang,
    isAffiliates
  })

  const monthlyAddonsData = buildMonthlyAddons(internetAddons, translate, lang)

  const additionalChargesData = buildAdditionalChargesList({
    internet: internetService,
    isInternetSmartHubService,
    lang
  })

  const additionalHSAddonChargesData = buildHSAddonsAdditionalCharges(internetAddons, lang)

  const additionalCreditsAndDiscountsData = buildAdditionalCreditsAndDiscounts({
    internet: internetService,
    cart,
    isIRPCFlow: IRPCParams.isIrpcFlow,
    isBusinessCart,
    lang
  })

  const recurringDiscounts = recurringDiscountsData?.map((discount) => (
    <NameAndPrice
      id={discount.id}
      key={discount.id}
      name={discount.name}
      price={discount.price}
      prefixIcon={discount.prefixIcon}
    />
  ))

  const monthlyAddons = (
    <HeadingWithSubItems headingText={translate('addons')} subItems={monthlyAddonsData} />
  )

  const includedHardware = (
    <HeadingWithSubItems
      headingText={translate('includedHardware')}
      subItems={includedInternetHardwareData}
    />
  )

  const additionalHardware = (
    <HeadingWithSubItems
      headingText={translate('devices.additionalDevices')}
      subItems={additionalInternetHardwareData}
    />
  )

  const essentialHardware = (
    <HeadingWithSubItems
      headingText={translate('essentialHardware')}
      subItems={essentialHardwareData}
    />
  )

  const additionalCharges = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCharges')}
      subItems={additionalChargesData}
    />
  )

  const additionalHSAddonCharges = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.addons')}
      subItems={additionalHSAddonChargesData}
    />
  )

  const additionalCreditsAndDiscounts = (
    <HeadingWithSubItems
      headingText={translate('additionalChargesAndCredits.additionalCreditsAndDiscounts')}
      subItems={additionalCreditsAndDiscountsData}
    />
  )

  return (
    <>
      <PriceBreakdownContainer>
        <ItemsPrice items={[internetService]} isRecurring />
        {recurringDiscounts}
        {monthlyAddons}
        {includedHardware}
        {additionalHardware}
        {shouldRenderEssentialHardwareInPriceBreakdown && essentialHardware}
        <FinalPrice price={monthlySubTotal} isMonthly />
      </PriceBreakdownContainer>
      <OtherChargesAndCreditsContainer
        listData={[
          ...(shouldRenderEssentialHardwareInOtherChargesAndCredits ? essentialHardwareData : []),
          ...additionalChargesData,
          ...additionalHSAddonChargesData,
          ...additionalCreditsAndDiscountsData
        ]}
      >
        {shouldRenderEssentialHardwareInOtherChargesAndCredits && essentialHardware}
        {additionalCharges}
        {additionalHSAddonCharges}
        {additionalCreditsAndDiscounts}
      </OtherChargesAndCreditsContainer>
    </>
  )
}

export default OfferDetails

OfferDetails.propTypes = {
  internetService: object.isRequired,
  cart: object.isRequired,
  internetAddons: array.isRequired,
  compassDiscountItem: object,
  featureFlags: object.isRequired,
  IRPCParams: object.isRequired,
  isBusinessCart: bool
}

OfferDetails.defaultProps = {
  compassDiscountItem: null,
  isBusinessCart: false
}
